import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  useColorModeValue,
  VStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Select,
  IconButton,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody,
} from "@chakra-ui/react";

import React, { useEffect, useMemo, useState } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import useAxios from "axios-hooks";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  FaChargingStation,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ViewIcon,
  DeleteIcon,
  CheckCircleIcon,
  WarningIcon,
  TimeIcon,
  InfoIcon,
  ExternalLinkIcon,
} from "@chakra-ui/icons";
import { motion } from "framer-motion";

// Custom components
import { deleteModalState } from "atoms/deleteModalState";
import DeleteModal from "components/modals/DeleteModal";
import LoadingSpinner from "components/feedback/LoadingSpinner";
import logger from "infra/logger";
import { accessLevels } from "utils/accessLevels";
import { loggedInUserState } from "atoms/loggedInUserState";
import RoleBasedRender from "components/wrappers/roleBasedRender";
import { onlyAdmins } from "utils/accessLevels";

// Motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionTr = motion(Tr);

// Styled components
const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)",
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)",
  );

  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "2xl" }}
      {...rest}
    >
      {children}
    </Box>
  );
};

const EmptyStateIllustration = () => (
  <MotionFlex
    direction="column"
    align="center"
    justify="center"
    py={12}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <Icon as={FaChargingStation} boxSize={16} color="#f57d36" mb={4} />
    <Text fontSize="xl" fontWeight="bold" mb={2}>
      No Charge Points Found
    </Text>
    <Text color="gray.500" textAlign="center" maxW="md">
      There are no charge points available at the moment. New charge points will
      appear here once added.
    </Text>
  </MotionFlex>
);

const StatusBadge = ({ status }) => {
  let color, icon, label;

  switch (status?.toLowerCase()) {
    case "online":
      color = "green";
      icon = CheckCircleIcon;
      label = "Online";
      break;
    case "offline":
    default:
      color = "red";
      icon = WarningIcon;
      label = "Offline";
  }

  return (
    <Badge
      display="flex"
      alignItems="center"
      px={3}
      py={1}
      borderRadius="full"
      colorScheme={color}
      variant="subtle"
    >
      <Icon as={icon} mr={1} boxSize={3} />
      <Text fontSize="xs" fontWeight="medium">
        {label}
      </Text>
    </Badge>
  );
};

const PaginationButton = ({ children, isActive, isDisabled, onClick }) => {
  return (
    <Button
      size="sm"
      variant={isActive ? "solid" : "outline"}
      colorScheme={isActive ? "orange" : "gray"}
      isDisabled={isDisabled}
      onClick={onClick}
      minW="40px"
      h="40px"
      borderRadius="md"
      _hover={!isDisabled && { transform: "translateY(-2px)", shadow: "md" }}
      transition="all 0.2s"
    >
      {children}
    </Button>
  );
};

export default function StationsTable(props) {
  const { columnsData } = props;
  const [chargePoints, setChargePoints] = useState([]);
  const [showDeleteModal, setShowDeleteModal] =
    useRecoilState(deleteModalState);
  const [selectedStation, setSelectedStation] = useState(null);
  const loggerInUser = useRecoilValue(loggedInUserState);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const columns = useMemo(() => columnsData, [columnsData]);
  const data = useMemo(() => chargePoints, [chargePoints]);

  const [{ chargePointsData, loading, error }, getPoints] = useAxios(
    { method: "GET", url: `/api/stations` },
    { manual: true },
  );

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0, // Start at page 0
        pageSize: 10, // Set the initial page size
      },
      globalFilter: (rows, ids, query) => {
        if (!query || query.trim() === "") {
          return rows;
        }

        const lowercasedQuery = query.toLowerCase().trim();

        return rows.filter((row) => {
          const stationId = row.original.StationId || "";
          const name = row.original.Name || "";
          const powerType = row.original.PowerType || "";
          const vendor = row.original.Vendor || "";
          const visibility = row.original.Visibility || "";
          const status = row.original.Status || "";

          return (
            stationId.toLowerCase().includes(lowercasedQuery) ||
            name.toLowerCase().includes(lowercasedQuery) ||
            powerType.toLowerCase().includes(lowercasedQuery) ||
            vendor.toLowerCase().includes(lowercasedQuery) ||
            visibility.toLowerCase().includes(lowercasedQuery) ||
            status.toLowerCase().includes(lowercasedQuery)
          );
        });
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { pageIndex, pageSize },
    setGlobalFilter,
    gotoPage,
    setPageSize,
  } = tableInstance;

  const { canPreviousPage, canNextPage, pageCount, nextPage, previousPage } =
    tableInstance;

  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const themeColor = "#f57d36";
  const theadBgColor = useColorModeValue("gray.50", "gray.800");

  const getChargePoints = async () => {
    try {
      const response = await getPoints();
      logger.log(response.data.data);
      setChargePoints(response.data.data);
    } catch (error) {
      logger.log(error);
    }
  };

  const handleDeleteClick = (station) => {
    setSelectedStation(station);
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    setSelectedStation(null);
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = () => {
    setShowDeleteModal(false);
    getChargePoints();
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setGlobalFilter(value);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get time since update
  const getTimeSince = (dateString) => {
    if (!dateString) return "Never";

    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    let interval = Math.floor(seconds / 31536000);
    if (interval > 1) return interval + " years ago";

    interval = Math.floor(seconds / 2592000);
    if (interval > 1) return interval + " months ago";

    interval = Math.floor(seconds / 86400);
    if (interval > 1) return interval + " days ago";

    interval = Math.floor(seconds / 3600);
    if (interval > 1) return interval + " hours ago";

    interval = Math.floor(seconds / 60);
    if (interval > 1) return interval + " minutes ago";

    return "Just now";
  };

  useEffect(() => {
    getChargePoints();
  }, []);

  return loading ? (
    <LoadingSpinner />
  ) : chargePoints.length > 0 ? (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      w="100%"
    >
      <GlassMorphismCard p={0}>
        {/* Search Section */}
        <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            gap={4}
          >
            <VStack
              align="start"
              spacing={2}
              width={{ base: "100%", md: "auto" }}
            >
              <Text fontSize="xs" fontWeight="medium" color="gray.500">
                Search Chargers
              </Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search by ID, name, power type, vendor..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  borderRadius="md"
                  width="350px"
                  _focus={{
                    borderColor: themeColor,
                    boxShadow: `0 0 0 1px ${themeColor}`,
                  }}
                />
              </InputGroup>
              <Text fontSize="xs" color="gray.500" mt={1} ml={1}>
                <Icon as={InfoIcon} boxSize={3} mr={1} />
                Type any keyword to search across all fields
              </Text>
            </VStack>

            <Flex align="center" justify="flex-end">
              <Text fontSize="xl" fontWeight="bold" color={textColor}>
                Chargers
              </Text>
              <Text ml={2} color="gray.500" fontSize="sm">
                {chargePoints.length} total
              </Text>
            </Flex>
          </Flex>
        </Box>

        <TableContainer>
          <Table {...getTableProps()} variant="simple" colorScheme="gray">
            <Thead bg={theadBgColor}>
              {headerGroups.map((headerGroup, index) => (
                <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={index}
                      py={4}
                      px={6}
                      borderColor={borderColor}
                      textTransform="uppercase"
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      color="gray.500"
                      textAlign="center"
                      cursor="pointer"
                      _hover={{ color: themeColor }}
                      transition="all 0.2s"
                    >
                      <Flex justify="center" align="center" gap={1}>
                        {column.render("Header")}
                        <Box>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <Icon as={FaSortDown} />
                            ) : (
                              <Icon as={FaSortUp} />
                            )
                          ) : (
                            <Icon as={FaSort} opacity={0.3} />
                          )}
                        </Box>
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {page.map((row, index) => {
                prepareRow(row);
                return (
                  <MotionTr
                    {...row.getRowProps()}
                    key={index}
                    whileHover={{ backgroundColor: hoverBg }}
                    transition={{ duration: 0.2 }}
                    _hover={{ bg: hoverBg }}
                    cursor="pointer"
                  >
                    {row.cells.map((cell, index) => {
                      let data = "";
                      if (cell.column.Header === "S.No") {
                        data = (
                          <Flex align="center" justify="center">
                            <Text
                              color={textColor}
                              fontSize="sm"
                              fontWeight="600"
                            >
                              {Number(cell.row.id) + 1}
                            </Text>
                          </Flex>
                        );
                      } else if (cell.column.Header === "ChargePoint") {
                        data = (
                          <Flex align="center" justify="flex-start" gap={2}>
                            <Badge
                              colorScheme={
                                cell.row.original.PowerType === "DC"
                                  ? "green"
                                  : "purple"
                              }
                              fontSize="xs"
                            >
                              {cell.row.original.PowerType || "N/A"}
                            </Badge>
                            <Text
                              color={textColor}
                              fontSize="sm"
                              fontWeight="600"
                            >
                              {cell.row.original.StationId}
                            </Text>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Description") {
                        data = (
                          <Flex align="center" justify="center">
                            <Text
                              color={textColor}
                              fontSize="sm"
                              fontWeight="600"
                            >
                              {cell.row.original.Name}
                            </Text>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Visibility") {
                        data = (
                          <Flex align="center" justify="center">
                            <Badge
                              colorScheme={
                                cell.row.original.Visibility === "PUBLIC"
                                  ? "blue"
                                  : "purple"
                              }
                              variant="subtle"
                              borderRadius="md"
                              px={2}
                            >
                              {cell.row.original.Visibility}
                            </Badge>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Status") {
                        const status = cell.row.original.Status;
                        data = (
                          <Flex align="center" justify="center">
                            <StatusBadge status={status || "Offline"} />
                          </Flex>
                        );
                      } else if (cell.column.Header === "Last Connected") {
                        const updatedAt = cell.row.original.UpdatedAt;
                        data = (
                          <Tooltip
                            label={formatDate(updatedAt)}
                            placement="top"
                            hasArrow
                          >
                            <Flex align="center" justify="center">
                              <Icon as={TimeIcon} mr={1} color="gray.500" />
                              <Text color={textColor} fontSize="xs">
                                {getTimeSince(updatedAt)}
                              </Text>
                            </Flex>
                          </Tooltip>
                        );
                      } else if (cell.column.Header === "Actions") {
                        const role = loggerInUser?.role;
                        const accessLevel = accessLevels[role]?.accessLevel;
                        const isDisabled = accessLevel > 2;
                        data = (
                          <Flex align="center" justify="center" gap={2}>
                            <Tooltip
                              label="View Details"
                              placement="top"
                              hasArrow
                            >
                              <IconButton
                                icon={<ViewIcon />}
                                colorScheme="blue"
                                variant="ghost"
                                size="sm"
                                borderRadius="full"
                                aria-label="View"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/admin/locations/view/${cell.row.original.LocationId}/stations?sid=${cell.row.original.StationId}`,
                                  );
                                }}
                              />
                            </Tooltip>

                            <RoleBasedRender roles={onlyAdmins}>
                              <Tooltip
                                label="Delete Charge Point"
                                placement="top"
                                hasArrow
                              >
                                <IconButton
                                  icon={<DeleteIcon />}
                                  colorScheme="red"
                                  variant="ghost"
                                  size="sm"
                                  borderRadius="full"
                                  aria-label="Delete"
                                  isDisabled={isDisabled}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(cell.row.original);
                                  }}
                                />
                              </Tooltip>
                            </RoleBasedRender>

                            <Popover
                              placement="left"
                              closeOnBlur={true}
                              autoFocus={false}
                              gutter={10}
                            >
                              <PopoverTrigger>
                                <IconButton
                                  icon={<InfoIcon />}
                                  colorScheme="gray"
                                  variant="ghost"
                                  size="sm"
                                  borderRadius="full"
                                  aria-label="More Info"
                                />
                              </PopoverTrigger>
                              <PopoverContent>
                                <PopoverArrow />
                                <PopoverCloseButton />
                                <PopoverHeader fontWeight="bold">
                                  Charge Point Details
                                </PopoverHeader>
                                <PopoverBody>
                                  <VStack align="start" spacing={2}>
                                    <Text fontSize="sm">
                                      <Text as="span" fontWeight="bold">
                                        Station ID:
                                      </Text>{" "}
                                      {cell.row.original.StationId}
                                    </Text>
                                    <Text fontSize="sm">
                                      <Text as="span" fontWeight="bold">
                                        Name:
                                      </Text>{" "}
                                      {cell.row.original.Name}
                                    </Text>
                                    <Text fontSize="sm">
                                      <Text as="span" fontWeight="bold">
                                        Power Type:
                                      </Text>{" "}
                                      <Badge
                                        colorScheme={
                                          cell.row.original.PowerType === "DC"
                                            ? "green"
                                            : "purple"
                                        }
                                        fontSize="xs"
                                      >
                                        {cell.row.original.PowerType || "N/A"}
                                      </Badge>
                                    </Text>
                                    <Text fontSize="sm">
                                      <Text as="span" fontWeight="bold">
                                        Max Power:
                                      </Text>{" "}
                                      {cell.row.original.MaxElectricPower ||
                                        "N/A"}{" "}
                                      kW
                                    </Text>
                                    <Text fontSize="sm">
                                      <Text as="span" fontWeight="bold">
                                        Vendor:
                                      </Text>{" "}
                                      {cell.row.original.Vendor || "N/A"}
                                    </Text>
                                    <Text fontSize="sm">
                                      <Text as="span" fontWeight="bold">
                                        Firmware:
                                      </Text>{" "}
                                      {cell.row.original.FirmwareVersion ||
                                        "N/A"}
                                    </Text>
                                    <Button
                                      size="xs"
                                      rightIcon={<ExternalLinkIcon />}
                                      colorScheme="orange"
                                      variant="outline"
                                      onClick={() =>
                                        navigate(
                                          `/admin/locations/view/${cell.row.original.LocationId}/stations?sid=${cell.row.original.StationId}`,
                                        )
                                      }
                                    >
                                      Full Details
                                    </Button>
                                  </VStack>
                                </PopoverBody>
                              </PopoverContent>
                            </Popover>
                          </Flex>
                        );
                      }
                      return (
                        <Td
                          {...cell.getCellProps()}
                          key={index}
                          py={4}
                          px={6}
                          borderColor={borderColor}
                          fontSize="sm"
                        >
                          {data}
                        </Td>
                      );
                    })}
                  </MotionTr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Flex
          p={4}
          justify="space-between"
          align="center"
          borderTop="1px solid"
          borderColor={borderColor}
          flexWrap="wrap"
          gap={4}
        >
          <HStack>
            <Select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              size="sm"
              borderRadius="md"
              w="120px"
            >
              {[5, 10, 20, 30, 40, 50].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" color="gray.500">
              Showing {page.length} of {chargePoints.length} results
            </Text>
          </HStack>

          <HStack spacing={2}>
            <PaginationButton
              isDisabled={!canPreviousPage}
              onClick={() => gotoPage(0)}
            >
              <ChevronLeftIcon boxSize={4} mr={1} />
              First
            </PaginationButton>

            <PaginationButton
              isDisabled={!canPreviousPage}
              onClick={() => previousPage()}
            >
              <ChevronLeftIcon boxSize={4} />
            </PaginationButton>

            {pageCount <= 5 ? (
              // If we have 5 or fewer pages, show all page buttons
              [...Array(pageCount)].map((_, i) => (
                <PaginationButton
                  key={i}
                  isActive={pageIndex === i}
                  onClick={() => gotoPage(i)}
                >
                  {i + 1}
                </PaginationButton>
              ))
            ) : (
              // If we have more than 5 pages, show a subset
              <>
                {pageIndex > 1 && (
                  <PaginationButton onClick={() => gotoPage(0)}>
                    1
                  </PaginationButton>
                )}

                {pageIndex > 2 && <Text>...</Text>}

                {pageIndex > 0 && (
                  <PaginationButton onClick={() => gotoPage(pageIndex - 1)}>
                    {pageIndex}
                  </PaginationButton>
                )}

                <PaginationButton isActive onClick={() => gotoPage(pageIndex)}>
                  {pageIndex + 1}
                </PaginationButton>

                {pageIndex < pageCount - 1 && (
                  <PaginationButton onClick={() => gotoPage(pageIndex + 1)}>
                    {pageIndex + 2}
                  </PaginationButton>
                )}

                {pageIndex < pageCount - 3 && <Text>...</Text>}

                {pageIndex < pageCount - 2 && (
                  <PaginationButton onClick={() => gotoPage(pageCount - 1)}>
                    {pageCount}
                  </PaginationButton>
                )}
              </>
            )}

            <PaginationButton
              isDisabled={!canNextPage}
              onClick={() => nextPage()}
            >
              <ChevronRightIcon boxSize={4} />
            </PaginationButton>

            <PaginationButton
              isDisabled={!canNextPage}
              onClick={() => gotoPage(pageCount - 1)}
            >
              Last
              <ChevronRightIcon boxSize={4} ml={1} />
            </PaginationButton>
          </HStack>
        </Flex>
      </GlassMorphismCard>

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        data={selectedStation}
        title="stations"
      />
    </MotionBox>
  ) : (
    <GlassMorphismCard w="100%" py={8}>
      <EmptyStateIllustration />
    </GlassMorphismCard>
  );
}
